import { parsePartialJson } from '@langchain/core/output_parsers';
import {
  ArtifactCodeV3,
  ArtifactMarkdownV3,
  ArtifactToolResponse,
  ArtifactType,
  ArtifactV3_Output,
  ProgrammingLanguageOptions,
  RewriteArtifactMetaToolResponse,
} from 'src/types';

// TODO(olha): WIP Editor from OC
export const extractStreamDataChunk = (chunk: unknown) => {
  if (Array.isArray(chunk)) {
    return chunk[1];
  }
  return chunk;
};

export const createNewGeneratedArtifactContent = (
  artifactTool: ArtifactToolResponse,
): ArtifactMarkdownV3 | ArtifactCodeV3 | undefined => {
  const { type, title = '', artifact = '', language } = artifactTool;

  if (!type) return;

  const base = {
    index: 1,
    type,
    title,
  };

  switch (type) {
    case ArtifactType.TEXT:
      return {
        ...base,
        fullMarkdown: artifact,
      };
    case ArtifactType.CODE:
      return {
        ...base,
        code: artifact,
        language: language as ProgrammingLanguageOptions,
      };
    default:
      return;
  }
};

// TODO(olha): WIP Editor from OC
export const handleGenerateArtifactToolCallChunk = (
  toolCallChunkArgs: string,
): ArtifactV3_Output | null | undefined => {
  let newArtifactText: ArtifactToolResponse | undefined = undefined;

  try {
    newArtifactText = parsePartialJson(toolCallChunkArgs);

    if (!newArtifactText) {
      throw new Error('Failed to parse new artifact text');
    }

    newArtifactText = {
      ...newArtifactText,
      title: newArtifactText.title ?? '',
      type: newArtifactText.type ?? undefined,
    };
  } catch (_) {
    return null;
  }

  if (
    newArtifactText.artifact &&
    (newArtifactText.type === ArtifactType.TEXT ||
      (newArtifactText.type === ArtifactType.CODE && newArtifactText.language))
  ) {
    const content = createNewGeneratedArtifactContent(newArtifactText);

    if (!content) {
      return undefined;
    }

    return {
      currentIndex: 1,
      contents: [content],
    };
  }
};

export const removeCodeBlockFormatting = (text: string): string => {
  const codeBlockRegex = /^```[\w-]*\n([\s\S]*?)\n```$/;

  const match = text.match(codeBlockRegex);

  if (match) {
    return match[1].trim();
  }

  return text;
};

export const createRewrittenArtifactContent = ({
  type,
  index,
  content,
  title,
  language,
}: {
  type: ArtifactType;
  index: number;
  content: string;
  title: string;
  language: string;
}): ArtifactMarkdownV3 | ArtifactCodeV3 => {
  const base = {
    type,
    index,
    title,
  };

  return type === ArtifactType.CODE
    ? {
        ...base,
        language: (language || 'other') as ProgrammingLanguageOptions,
        code: content,
      }
    : {
        ...base,
        fullMarkdown: content,
      };
};

export const getRewrittenArtifact = ({
  prevArtifactOutput,
  newContent,
  rewriteArtifactMeta,
}: {
  prevArtifactOutput: ArtifactV3_Output;
  newContent: string;
  rewriteArtifactMeta: RewriteArtifactMetaToolResponse;
}): ArtifactV3_Output => {
  const basePrevArtifact: ArtifactV3_Output = {
    ...prevArtifactOutput,
    contents: prevArtifactOutput.contents.map((item) => ({ ...item })),
  };

  const newIndex = prevArtifactOutput.contents.length + 1;

  const title =
    rewriteArtifactMeta.title ??
    prevArtifactOutput.contents[prevArtifactOutput.contents.length - 1]
      ?.title ??
    '';

  const content =
    rewriteArtifactMeta.type === ArtifactType.CODE
      ? removeCodeBlockFormatting(newContent)
      : newContent;

  const newEntry = createRewrittenArtifactContent({
    type: rewriteArtifactMeta.type,
    index: newIndex,
    content: content,
    title,
    language: rewriteArtifactMeta.language as ProgrammingLanguageOptions,
  });

  const contents = [...basePrevArtifact.contents, newEntry];

  const newArtifact: ArtifactV3_Output = {
    ...basePrevArtifact,
    currentIndex: newIndex,
    contents,
  };

  return newArtifact;
};

export const updateHighlightedMarkdown = ({
  prevArtifactOutput,
  newContent,
}: {
  prevArtifactOutput: ArtifactV3_Output;
  newContent: string;
}): ArtifactV3_Output => {
  const basePrevArtifact: ArtifactV3_Output = {
    ...prevArtifactOutput,
    contents: prevArtifactOutput.contents.map((item) => ({ ...item })),
  };

  const newIndex = prevArtifactOutput.contents.length + 1;

  const title =
    prevArtifactOutput.contents[prevArtifactOutput.contents.length - 1]
      ?.title ?? '';

  const newContents = [
    ...basePrevArtifact.contents,
    {
      type: ArtifactType.TEXT,
      title,
      index: newIndex,
      fullMarkdown: newContent,
    },
  ];

  const newArtifact: ArtifactV3_Output = {
    ...basePrevArtifact,
    currentIndex: newIndex,
    contents: newContents,
  };

  return newArtifact;
};

export const updateHighlightedCode = ({
  prevArtifactOutput,
  newContent,
  language,
}: {
  prevArtifactOutput: ArtifactV3_Output;
  newContent: string;
  language: ProgrammingLanguageOptions;
}): ArtifactV3_Output => {
  const basePrevArtifact = {
    ...prevArtifactOutput,
    contents: prevArtifactOutput.contents.map((item) => ({ ...item })),
  };

  const newIndex = prevArtifactOutput.contents.length + 1;

  const title =
    prevArtifactOutput.contents[prevArtifactOutput.contents.length - 1]
      ?.title ?? '';

  const newContents = [
    ...basePrevArtifact.contents,
    {
      type: ArtifactType.CODE,
      title,
      index: newIndex,
      code: newContent,
      language,
    },
  ];

  const newArtifact: ArtifactV3_Output = {
    ...basePrevArtifact,
    currentIndex: newIndex,
    contents: newContents,
  };

  return newArtifact;
};
